"use client";

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Card } from "@/components/ui-system";
import { ThemedSelect, themedSelectTriggerClass } from "@/components/ui-system/select";
import {
  getWorkspaceActivityPolicy,
  updateWorkspaceActivityPolicy,
  type ActivityPolicyMode,
  type WorkspaceActivityPolicyDto,
} from "@/lib/api/endpoints/activity-policy";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";

type PolicyDraft = Pick<
  WorkspaceActivityPolicyDto,
  "mode" | "default_window_seconds" | "sampling_multiplier" | "min_window_seconds" | "max_window_seconds"
>;

function toDraft(policy: WorkspaceActivityPolicyDto): PolicyDraft {
  return {
    mode: policy.mode,
    default_window_seconds: policy.default_window_seconds,
    sampling_multiplier: policy.sampling_multiplier,
    min_window_seconds: policy.min_window_seconds,
    max_window_seconds: policy.max_window_seconds,
  };
}

function minutes(seconds: number): number {
  return Math.round((seconds / 60) * 100) / 100;
}

function secondsFromMinutes(value: number): number {
  return Math.max(60, Math.round(value * 60));
}

export function ActivityPolicyPage() {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const tenantId = useAuthStore((s) => s.tenantId);
  const { permissionMap } = useEffectivePermissions();
  const isSuperAdmin = user?.role === "super_admin";
  const isPlatformActor = isSuperAdmin || user?.accountType === "platform";
  const canEdit = hasModulePermission(user, permissionMap, "activity_policy", "edit");
  const [draft, setDraft] = useState<PolicyDraft | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const effectiveTenantId = tenantId;
  const requiresWorkspaceSelection = isPlatformActor && !effectiveTenantId;

  const query = useQuery({
    queryKey: ["activity-policy", "workspace", effectiveTenantId ?? "active"],
    queryFn: () => getWorkspaceActivityPolicy(isSuperAdmin ? effectiveTenantId : null),
    enabled: Boolean(effectiveTenantId) && !requiresWorkspaceSelection,
    staleTime: 30_000,
  });

  useEffect(() => {
    setDraft(null);
    setMessage(null);
  }, [effectiveTenantId]);

  useEffect(() => {
    if (query.data) {
      setDraft(toDraft(query.data));
    }
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (body: PolicyDraft) => updateWorkspaceActivityPolicy(body, isSuperAdmin ? effectiveTenantId : null),
    onSuccess: async (policy) => {
      setDraft(toDraft(policy));
      setMessage("Logger activity policy saved.");
      await queryClient.invalidateQueries({ queryKey: ["activity-policy", "workspace"] });
      await queryClient.invalidateQueries({ queryKey: ["platform-overview"] });
      await queryClient.invalidateQueries({ queryKey: ["workspace-overview"] });
    },
    onError: (error) => {
      setMessage(error instanceof Error ? error.message : "Could not save logger activity policy.");
    },
  });

  const dirty = useMemo(() => {
    if (!draft || !query.data) return false;
    return JSON.stringify(draft) !== JSON.stringify(toDraft(query.data));
  }, [draft, query.data]);

  const activeWindowPreview = useMemo(() => {
    if (!draft) return "";
    if (draft.mode === "fixed_window") {
      return `Every logger is inactive after ${minutes(draft.default_window_seconds)} minutes without telemetry.`;
    }
    return `A logger using 1 sample/minute is inactive after ${minutes(
      Math.max(draft.min_window_seconds, Math.min(draft.max_window_seconds, 60 * draft.sampling_multiplier)),
    )} minutes.`;
  }, [draft]);

  function patch(next: Partial<PolicyDraft>) {
    setDraft((current) => (current ? { ...current, ...next } : current));
    setMessage(null);
  }

  return (
    <div className="mx-auto w-full max-w-workspace px-ds-3 pb-ds-8 pt-ds-3 sm:px-ds-4 md:pt-ds-4 lg:px-ds-5">
      {query.isError ? (
        <Card className="mb-ds-4 border-error/25 bg-error/5 p-ds-4 text-error">
          {query.error instanceof Error ? query.error.message : "Could not load logger activity policy."}
        </Card>
      ) : null}

      <Card className="space-y-ds-5 p-ds-5">
        <div className="flex flex-wrap items-start justify-between gap-ds-3">
          <div>
            <p className="text-title font-semibold text-foreground">Logger activity policy</p>
            <p className="mt-ds-1 text-caption text-muted">
              Controls how overview cards classify data loggers as live or inactive.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-ds-2">
            {message ? <span className="text-caption text-muted">{message}</span> : null}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={!dirty || mutation.isPending || !draft}
              onClick={() => query.data && setDraft(toDraft(query.data))}
            >
              Reset
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!canEdit || !dirty || mutation.isPending || !draft}
              onClick={() => draft && mutation.mutate(draft)}
            >
              {mutation.isPending ? "Saving..." : "Save policy"}
            </button>
          </div>
        </div>

        {requiresWorkspaceSelection ? (
          <div className="rounded-card border border-border bg-surface-secondary px-ds-4 py-ds-3 text-caption text-muted">
            Select one workspace in the app header to view or edit its logger activity policy.
          </div>
        ) : null}

        {!canEdit ? (
          <div className="rounded-card border border-card-border bg-surface-secondary px-ds-4 py-ds-3 text-caption text-muted">
            You can view this policy, but editing requires logger activity edit permission.
          </div>
        ) : null}

        {!draft ? (
          <div className="rounded-card border border-card-border bg-surface-secondary px-ds-4 py-ds-6 text-caption text-muted">
            Loading logger activity policy...
          </div>
        ) : (
          <>
            <div className="rounded-control border border-primary/15 bg-primary/5 px-ds-4 py-ds-3 text-caption text-foreground">
              {activeWindowPreview}
            </div>

            <div className="grid gap-ds-4 lg:grid-cols-2">
              <label className="space-y-ds-1">
                <span className="form-label">Activity mode</span>
                <ThemedSelect
                  value={draft.mode}
                  disabled={!canEdit}
                  onChange={(value) => patch({ mode: value as ActivityPolicyMode })}
                  options={[
                    {
                      value: "sampling_metadata",
                      label: "Use logger sampling metadata",
                      description: "Window = sampling interval x multiplier, clamped by min/max.",
                    },
                    {
                      value: "fixed_window",
                      label: "Use fixed workspace window",
                      description: "All loggers use the same inactive threshold.",
                    },
                  ]}
                  ariaLabel="Activity calculation mode"
                  triggerClassName={themedSelectTriggerClass}
                  menuClassName="rounded-card border-card-border bg-surface-elevated p-1.5"
                  optionClassName="rounded-control text-caption"
                />
              </label>
              <NumberField
                label="Default / fixed inactive window"
                suffix="minutes"
                value={minutes(draft.default_window_seconds)}
                disabled={!canEdit}
                min={1}
                max={1440}
                step={1}
                onChange={(value) => patch({ default_window_seconds: secondsFromMinutes(value) })}
              />
              <NumberField
                label="Sampling multiplier"
                suffix="x"
                value={draft.sampling_multiplier}
                disabled={!canEdit || draft.mode === "fixed_window"}
                min={1}
                max={20}
                step={0.5}
                onChange={(value) => patch({ sampling_multiplier: value })}
              />
              <NumberField
                label="Minimum activity window"
                suffix="minutes"
                value={minutes(draft.min_window_seconds)}
                disabled={!canEdit}
                min={1}
                max={1440}
                step={1}
                onChange={(value) => patch({ min_window_seconds: secondsFromMinutes(value) })}
              />
              <NumberField
                label="Maximum activity window"
                suffix="minutes"
                value={minutes(draft.max_window_seconds)}
                disabled={!canEdit}
                min={1}
                max={10080}
                step={1}
                onChange={(value) => patch({ max_window_seconds: secondsFromMinutes(value) })}
              />
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function NumberField({
  label,
  suffix,
  value,
  disabled,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  suffix: string;
  value: number;
  disabled: boolean;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="space-y-ds-1 rounded-card border border-card-border bg-surface-primary p-ds-4">
      <span className="form-label">{label}</span>
      <div className="flex items-center gap-ds-2">
        <input
          type="number"
          className="ui-control h-10 min-w-0 flex-1 rounded-control bg-surface-primary px-ds-3 text-caption"
          value={value}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          onChange={(event) => {
            const next = Number(event.target.value);
            if (Number.isFinite(next)) {
              onChange(Math.max(min, Math.min(max, next)));
            }
          }}
        />
        <span className="min-w-[4.5rem] text-caption text-muted">{suffix}</span>
      </div>
    </label>
  );
}
